// src/utils/excelExporter.js
import { write, writeFile, utils } from 'xlsx';
import { DETECTOR_NAME_MAP, buildTableTitle } from '../components/DataTable/chartSeriesUtils';

/**
 * 安全转换值（处理null/undefined/对象）
 */
const safeValue = (val) => {
  if (val == null) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val).trim();
};

const EXCEL_TYPE_LABELS = {
  xlsx: 'Excel 工作簿 (*.xlsx)',
  xls: 'Excel 97-2004 工作簿 (*.xls)',
};

const sanitizeFileName = (value = '', fallback = 'EMC数据导出', maxLength = 120) => {
  const normalized = String(value)
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .trim();

  return (normalized || fallback).substring(0, maxLength);
};

const sanitizeSheetName = (value = '', fallback = 'Sheet1') => {
  const normalized = String(value)
    .replace(/[\]\\/*?:[]/g, '_')
    .trim();

  return (normalized || fallback).substring(0, 31);
};

const TRAILING_COLUMN_ORDER = ['带宽[kHz]', '检波器', '原始公式'];

const reorderColumns = (columns = []) => {
  const trailingColumns = TRAILING_COLUMN_ORDER.filter((column) => columns.includes(column));
  const baseColumns = columns.filter(
    (column) => column !== '删除' && !TRAILING_COLUMN_ORDER.includes(column)
  );
  return [...baseColumns, '删除', ...trailingColumns];
};

const getWorksheetData = (columns = [], rows = []) => {
  const exportColumns = reorderColumns(columns);
  const data = [exportColumns];

  rows.forEach((row) => {
    const rowData = exportColumns.map((col) => {
      if (col === '删除') {
        return 'System.Drawing.Bitmap';
      }

      const val = row[col];
      return val === null || val === undefined || val === '' ? '-' : safeValue(val);
    });

    data.push(rowData);
  });

  return data;
};

const buildSheetName = (table = {}, index = 0, usedSheetNames = new Set(), context = {}) => {
  const { metadata = {} } = table;
  const titleFromWeb = buildTableTitle({
    deviceType: context.deviceType,
    device_type: table?.device_type,
    metadata,
    index,
    documentType: context.documentType,
  });
  const noteLabel = DETECTOR_NAME_MAP[metadata.note] || metadata.note;
  const candidates = [titleFromWeb, noteLabel, metadata.direction, metadata.sheetName, `Sheet${index + 1}`]
    .map((item) => String(item || '').trim())
    .filter(Boolean);

  let baseName = sanitizeSheetName(candidates[0], `Sheet${index + 1}`);
  let uniqueName = baseName;
  let counter = 2;

  while (usedSheetNames.has(uniqueName)) {
    const suffix = `_${counter}`;
    uniqueName = `${baseName.substring(0, 31 - suffix.length)}${suffix}`;
    counter += 1;
  }

  usedSheetNames.add(uniqueName);
  return uniqueName;
};

const buildWorkbook = (tables = [], context = {}) => {
  const workbook = utils.book_new();
  const usedSheetNames = new Set();

  tables.forEach((table, index) => {
    if (!table?.columns || !Array.isArray(table.rows) || table.rows.length === 0) {
      return;
    }

    const worksheet = utils.aoa_to_sheet(getWorksheetData(table.columns, table.rows));
    const sheetName = buildSheetName(table, index, usedSheetNames, context);
    utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  return workbook;
};

const buildBaseFileName = (fileNamePrefix = 'EMC数据', level = '', detector = '') => {
  const cleanBase = sanitizeFileName(fileNamePrefix, 'EMC数据导出', 60);
  const isVehicleStandardExport = cleanBase.startsWith('车辆国标国际标准') || cleanBase.startsWith('车辆企业标准');

  let vehicleSuffix = '';
  if (isVehicleStandardExport) {
    const parts = [];
    if (level?.trim()) parts.push(sanitizeFileName(level.trim(), '', 30));
    const detectorLabel = DETECTOR_NAME_MAP[detector?.trim()] || detector?.trim();
    if (detectorLabel) parts.push(sanitizeFileName(detectorLabel, '', 30));
    vehicleSuffix = parts.join('_');
  }

  const dateStr = new Date().toISOString().split('T')[0];
  return sanitizeFileName([cleanBase, vehicleSuffix, dateStr].filter(Boolean).join('_'));
};

const getPickerOptions = (suggestedName) => ({
  suggestedName,
  types: [
    {
      description: EXCEL_TYPE_LABELS.xlsx,
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      },
    },
    {
      description: EXCEL_TYPE_LABELS.xls,
      accept: {
        'application/vnd.ms-excel': ['.xls'],
      },
    },
  ],
  excludeAcceptAllOption: false,
});

const getExtensionFromHandle = (handle, fallback = 'xlsx') => {
  const fileName = handle?.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension === 'xls' || extension === 'xlsx' ? extension : fallback;
};

const saveWorkbookWithPicker = async (workbook, suggestedBaseName) => {
  const suggestedName = `${suggestedBaseName}.xlsx`;

  if (typeof window.showSaveFilePicker !== 'function') {
    writeFile(workbook, suggestedName, { bookType: 'xlsx' });
    return { fileName: suggestedName, fileType: 'xlsx', usedPicker: false };
  }

  const handle = await window.showSaveFilePicker(getPickerOptions(suggestedName));
  const fileType = getExtensionFromHandle(handle, 'xlsx');
  const buffer = write(workbook, { bookType: fileType, type: 'array' });
  const writable = await handle.createWritable();
  await writable.write(buffer);
  await writable.close();

  return { fileName: handle.name || `${suggestedBaseName}.${fileType}`, fileType, usedPicker: true };
};

/**
 * 主导出函数：将多个表格保存到同一个 Excel 文件的不同 sheet。
 * @param {Array} tables - 表格数组（每个含columns/rows/metadata）
 * @param {string} fileNamePrefix - 基础文件名前缀
 * @param {string} level - 车辆报告等级（用于文件名）
 * @param {string} detector - 车辆报告检波器（用于文件名）
 */
export const exportToExcel = async (tables, fileNamePrefix = 'EMC数据', level = '', detector = '', context = {}) => {
  if (!tables?.length) {
    alert('没有数据可导出');
    return;
  }

  const validTables = tables.filter((table) => table?.columns && Array.isArray(table.rows) && table.rows.length > 0);
  if (!validTables.length) {
    alert('没有可导出的有效表格');
    return;
  }

  try {
    const workbook = buildWorkbook(validTables, context);
    const suggestedBaseName = buildBaseFileName(fileNamePrefix, level, detector);
    await saveWorkbookWithPicker(workbook, suggestedBaseName);
    alert('保存成功');
  } catch (err) {
    if (err?.name === 'AbortError') {
      return;
    }

    console.error('导出失败:', err);
    alert(`导出失败: ${err.message || '未知错误'}`);
  }
};